import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Popconfirm,
  App,
  Switch,
  Tag,
  Modal,
  Form,
  Input,
} from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import api from '../../../services/api';
import { PropertyType } from '../../../types/property';

const PropertyTypesList: React.FC = () => {
  const [propertyTypes, setPropertyTypes] = useState<PropertyType[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingType, setEditingType] = useState<PropertyType | null>(null);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const { message } = App.useApp();

  const fetchPropertyTypes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('property-types');
      setPropertyTypes(response.data.data || []);
    } catch (error) {
      message.error('Gagal mengambil data property types');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    fetchPropertyTypes();
  }, [fetchPropertyTypes]);

  const showModal = (type: PropertyType | null = null) => {
    setEditingType(type);
    form.setFieldsValue(
      type
        ? { name: type.name, description: type.description }
        : { name: '', description: '' }
    );
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingType(null);
    form.resetFields();
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      if (editingType) {
        await api.put(`property-types/${editingType.id}`, values);
        message.success('Property type berhasil diperbarui');
      } else {
        await api.post('property-types', values);
        message.success('Property type berhasil ditambahkan');
      }

      fetchPropertyTypes();
      handleCancel();
    } catch (error) {
      message.error('Operasi gagal. Silakan coba lagi.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (record: PropertyType) => {
    try {
      setLoading(true);
      const action = record.is_active ? 'deactivated' : 'reactivated';
      const endpoint = record.is_active
        ? `/property-types/${record.id}`
        : `/property-types/${record.id}/reactivate`;

      if (record.is_active) {
        await api.delete(endpoint);
      } else {
        await api.put(endpoint);
      }

      message.success(`Property type ${action} berhasil`);
      fetchPropertyTypes();
    } catch (error) {
      const action = record.is_active ? 'deactivate' : 'reactivate';
      message.error(`Gagal ${action} property type`);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Nama Property Type',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: PropertyType, b: PropertyType) => a.name.localeCompare(b.name),
    },
    {
      title: 'Deskripsi',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 120,
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Aksi',
      key: 'actions',
      width: 150,
      render: (_: any, record: PropertyType) => (
        <Space size="middle">
          <Button
            icon={<EditOutlined />}
            onClick={() => showModal(record)}
            disabled={!record.is_active}
          />
          <Popconfirm
            title={`Apakah Anda yakin ingin ${
              record.is_active ? 'deactivate' : 'reactivate'
            } tipe ini?`}
            onConfirm={() => handleToggleActive(record)}
            okText="Ya"
            cancelText="Tidak"
          >
            <Switch
              checked={record.is_active}
              checkedChildren="Active"
              unCheckedChildren="Inactive"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Property Types"
      bordered={false}
      extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => showModal()}
        >
          Tambah
        </Button>
      }
    >
      <Table
        columns={columns}
        dataSource={propertyTypes}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title={editingType ? 'Edit Property Type' : 'Tambah Property Type Baru'}
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        confirmLoading={loading}
        destroyOnClose
      >
        <Form form={form} layout="vertical" name="propertyTypeForm">
          <Form.Item
            name="name"
            label="Nama Property Type"
            rules={[{ required: true, message: 'Harap masukkan nama!' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="description"
            label="Deskripsi"
          >
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default PropertyTypesList;
